import { Component } from '@theme/component';

/**
 * Calculates peptide reconstitution concentration and syringe draw volume
 * from vial amount, bacteriostatic water volume, desired dose, and syringe size.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} vialAmount
 * @property {HTMLInputElement} bacWater
 * @property {HTMLInputElement} desiredDose
 * @property {HTMLSelectElement} syringeSize
 * @property {HTMLElement} concentrationOutput
 * @property {HTMLElement} volumeOutput
 * @property {HTMLElement} unitsOutput
 * @property {HTMLElement} dosesPerVialOutput
 * @property {HTMLElement} syringeFill
 * @property {HTMLElement} syringeWarning
 *
 * @extends Component<Refs>
 */
export class DosageCalculator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.calculate();
  }

  calculate = () => {
    const { refs } = this;

    const vialMg = Number(refs.vialAmount?.value) || 0;
    const bacWaterMl = Number(refs.bacWater?.value) || 0;
    const doseMg = Number(refs.desiredDose?.value) || 0;
    const doseMcg = doseMg * 1000;
    const syringeUnits = Number(refs.syringeSize?.value) || 100;

    if (vialMg <= 0 || bacWaterMl <= 0) {
      this.#setText(refs.concentrationOutput, '—');
      this.#setText(refs.volumeOutput, '—');
      this.#setText(refs.unitsOutput, '—');
      this.#setText(refs.dosesPerVialOutput, '—');
      this.#setFill(0);
      this.#setWarning(false);
      return;
    }

    const concentrationMcgPerMl = (vialMg * 1000) / bacWaterMl;
    this.#setText(refs.concentrationOutput, `${this.#round(concentrationMcgPerMl)} mcg/mL`);

    if (doseMcg <= 0) {
      this.#setText(refs.volumeOutput, '—');
      this.#setText(refs.unitsOutput, '—');
      this.#setText(refs.dosesPerVialOutput, '—');
      this.#setFill(0);
      this.#setWarning(false);
      return;
    }

    const volumeMl = doseMcg / concentrationMcgPerMl;
    const units = volumeMl * syringeUnits;
    const dosesPerVial = (vialMg * 1000) / doseMcg;

    this.#setText(refs.volumeOutput, `${volumeMl.toFixed(3)} mL`);
    this.#setText(refs.unitsOutput, `${this.#round(units)} units`);
    this.#setText(refs.dosesPerVialOutput, `${Math.floor(dosesPerVial)} doses`);

    this.#setFill(syringeUnits ? Math.min((units / syringeUnits) * 100, 100) : 0);
    this.#setWarning(units > syringeUnits);
  };

  /**
   * @param {number} value
   */
  #round(value) {
    return Math.round(value * 10) / 10;
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string} text
   */
  #setText(element, text) {
    if (element) element.textContent = text;
  }

  /** @param {number} percent */
  #setFill(percent) {
    if (this.refs.syringeFill) this.refs.syringeFill.style.width = `${percent}%`;
  }

  /** @param {boolean} show */
  #setWarning(show) {
    if (this.refs.syringeWarning) this.refs.syringeWarning.hidden = !show;
  }
}

if (!customElements.get('dosage-calculator')) {
  customElements.define('dosage-calculator', DosageCalculator);
}
